import type {
  CatalogMaterial,
  CatalogTool,
  ProjectDetail,
  StepMaterialRef,
  StepNode,
  UpdateProjectRequest,
  UpdateStepRequest,
} from '@/api/types'

/** Walks the recursive tree, returning a new tree with the targeted step's fields patched. */
const mapStepTree = (steps: StepNode[], visit: (s: StepNode) => StepNode): StepNode[] =>
  steps.map((step) => {
    const patched = visit(step)
    if (patched.children !== step.children || patched === step) {
      // Mapped node may itself need its children re-walked; do that unconditionally.
    }
    return { ...patched, children: mapStepTree(patched.children, visit) }
  })

/**
 * Apply the simple, non-structural fields of an UpdateStepRequest to the matching step in the
 * tree. Callers must skip the optimistic path when the patch touches sortOrder / parentStepId /
 * clearParent — restructuring the tree optimistically is not supported here.
 */
export const patchStepInTree = (
  detail: ProjectDetail,
  stepId: string,
  data: UpdateStepRequest
): ProjectDetail => ({
  ...detail,
  steps: mapStepTree(detail.steps, (step) => {
    if (step.id !== stepId) return step
    const next: StepNode = { ...step }
    if (data.title !== undefined) next.title = data.title
    if (data.description !== undefined) {
      next.description = data.description === '' ? null : data.description
    }
    return next
  }),
})

/** Update one StepMaterial's quantity (or other simple fields) anywhere in the tree. */
export const patchStepMaterialInTree = (
  detail: ProjectDetail,
  stepMaterialId: string,
  partial: Partial<Pick<StepMaterialRef, 'quantity'>>
): ProjectDetail => ({
  ...detail,
  steps: mapStepTree(detail.steps, (step) => {
    if (!step.materials.some((sm) => sm.id === stepMaterialId)) return step
    return {
      ...step,
      materials: step.materials.map((sm) =>
        sm.id === stepMaterialId ? { ...sm, ...partial } : sm
      ),
    }
  }),
})

/** Apply the simple project-level fields (title / description / visibility). */
export const patchProjectFields = (
  detail: ProjectDetail,
  data: UpdateProjectRequest
): ProjectDetail => {
  const next: ProjectDetail = { ...detail }
  if (data.title !== undefined) next.title = data.title
  if (data.description !== undefined) {
    next.description = data.description === '' ? null : data.description
  }
  if (data.visibility !== undefined) next.visibility = data.visibility
  return next
}

/** Update a single catalog material's simple fields. */
export const patchCatalogMaterial = (
  detail: ProjectDetail,
  materialId: string,
  partial: Partial<Pick<CatalogMaterial, 'name' | 'unit' | 'unitPriceCents' | 'externalUrl'>>
): ProjectDetail => ({
  ...detail,
  catalog: {
    ...detail.catalog,
    materials: detail.catalog.materials.map((m) =>
      m.id === materialId ? { ...m, ...partial } : m
    ),
  },
})

/** Update a single catalog tool's simple fields. */
export const patchCatalogTool = (
  detail: ProjectDetail,
  toolId: string,
  partial: Partial<Pick<CatalogTool, 'name' | 'unitPriceCents' | 'externalUrl'>>
): ProjectDetail => ({
  ...detail,
  catalog: {
    ...detail.catalog,
    tools: detail.catalog.tools.map((t) => (t.id === toolId ? { ...t, ...partial } : t)),
  },
})

/**
 * Convenience: which UpdateStepRequest patches must NOT be applied optimistically. Restructuring
 * (parent / sort) and Phase 2 kind / milestone changes both go through invalidate-then-refetch —
 * the server may reject them based on state we don't fully model client-side (composite-FK
 * conversion guards, milestone-only-on-Section-or-Reference, etc.), and a refetch is the
 * cleanest way to reconverge.
 */
export const isStructuralStepUpdate = (data: UpdateStepRequest): boolean =>
  data.parentStepId !== undefined ||
  data.clearParent !== undefined ||
  data.sortOrder !== undefined ||
  data.kind !== undefined ||
  data.isMilestone !== undefined

/**
 * Inject a newly-created StepNode into the cached tree at the right location so the outline
 * re-renders immediately, without waiting for invalidate-and-refetch. Used by useCreateStep's
 * onSuccess — the server response IS the authoritative new node, so this is reconciled, not
 * speculative. Falls back to a no-op if the parent can't be found (caller should still
 * invalidate as a belt-and-suspenders guard).
 */
export const appendStepToTree = (detail: ProjectDetail, newStep: StepNode): ProjectDetail => {
  if (newStep.parentStepId === null) {
    return { ...detail, steps: [...detail.steps, newStep] }
  }
  let inserted = false
  const inject = (steps: StepNode[]): StepNode[] =>
    steps.map((step) => {
      if (step.id === newStep.parentStepId) {
        inserted = true
        return { ...step, children: [...step.children, newStep] }
      }
      if (step.children.length === 0) return step
      return { ...step, children: inject(step.children) }
    })
  const nextSteps = inject(detail.steps)
  return inserted ? { ...detail, steps: nextSteps } : detail
}

/**
 * Remove a Step from the cached tree wherever it lives (root or nested). Used by
 * useDeleteStep's onSuccess so the outline drops the row immediately on click — no
 * waiting on invalidate-and-refetch.
 */
export const removeStepFromTree = (detail: ProjectDetail, stepId: string): ProjectDetail => {
  const prune = (steps: StepNode[]): StepNode[] =>
    steps
      .filter((step) => step.id !== stepId)
      .map((step) =>
        step.children.length === 0 ? step : { ...step, children: prune(step.children) }
      )
  return { ...detail, steps: prune(detail.steps) }
}
